import type { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { HttpContext, HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import type { Observable } from 'rxjs';
import { catchError, throwError } from 'rxjs';

/**
 * Standardized Error Response Interface
 *
 * Matches the backend API response format for consistency
 * Ensures all error responses follow the same structure
 */
export interface StandardizedErrorResponse {
  success: false;
  statusCode: number;
  message: string;
  error: string;
  timestamp: string;
  path: string;
}

/**
 * When set on a request, HTTP 404 does not open the global auth-error snack bar.
 * Use for optional resources where 404 means "no data" (e.g. GET patient lost-info).
 */
export const SKIP_AUTH_ERROR_SNACKBAR_FOR_NOT_FOUND = new HttpContextToken<boolean>(() => false);

/** HttpContext with only {@link SKIP_AUTH_ERROR_SNACKBAR_FOR_NOT_FOUND}; merge via {@link HttpContext.set} on other contexts. */
export const withSkipAuthErrorSnackbarForNotFound = (): HttpContext =>
  new HttpContext().set(SKIP_AUTH_ERROR_SNACKBAR_FOR_NOT_FOUND, true);

/**
 * Authentication Error Interceptor
 *
 * Standardizes error responses across all HTTP requests and provides
 * clear, consistent error messages for different authentication scenarios.
 *
 * Features:
 * - Converts all HTTP errors to a standardized format
 * - Provides specific error messages for authentication failures
 * - Logs authentication errors for debugging and monitoring
 * - Distinguishes between token expiration and other 401 errors
 *
 * Error Codes:
 * - TOKEN_EXPIRED_OR_INVALID: Bearer token has expired or is invalid (401)
 * - INSUFFICIENT_PERMISSIONS: User lacks required permissions (403)
 * - NOT_FOUND: Requested resource not found (404)
 * - SERVER_ERROR: Server-side error occurred (5xx)
 * - NETWORK_ERROR: Network connectivity issue
 *
 * @example
 * This interceptor is registered in app.config.ts:
 * const interceptors = [..., authErrorInterceptor, ...];
 *
 * Usage in components:
 * ```typescript
 * this.apiService.getData().subscribe({
 *   next: (data) => {...},
 *   error: (error: StandardizedErrorResponse) => {
 *     if (error.error === 'TOKEN_EXPIRED_OR_INVALID') {
 *       // Token refresh interceptor will handle this
 *     }
 *   }
 * });
 * ```
 *
 * @see see token-refresh.interceptor.ts for automatic token refresh handling
 */
export const authErrorInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const snackBar = inject(MatSnackBar);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Standardize the error response
      const standardizedError = standardizeHttpError(error);

      const skipSnackBarForExpectedNotFound: boolean =
        error.status === 404 && req.context.get(SKIP_AUTH_ERROR_SNACKBAR_FOR_NOT_FOUND);

      // 409 conflicts are usually shown inline on forms (e.g. duplicate name).
      // Optional singleton reads may use SKIP_AUTH_ERROR_SNACKBAR_FOR_NOT_FOUND for HTTP 404 = no row.
      if (error.status !== 409 && !skipSnackBarForExpectedNotFound) {
        snackBar.open(standardizedError.message, 'Dismiss', { duration: 6000 });
      }

      // Log authentication-specific errors
      if (error.status === 401) {
        console.warn('[Auth Error] Authentication token issue', {
          path: req.url,
          method: req.method,
          message: standardizedError.message,
          timestamp: standardizedError.timestamp,
        });
      }

      // Log all other HTTP errors
      if (error.status >= 400) {
        const logLevel = error.status >= 500 ? 'error' : 'warn';
        console[logLevel]('[HTTP Error] Request failed', {
          statusCode: error.status,
          message: standardizedError.message,
          errorCode: standardizedError.error,
          path: req.url,
          method: req.method,
          timestamp: standardizedError.timestamp,
        });
      }

      // Return the standardized error
      return throwError(() => standardizedError);
    })
  );
};

/**
 * Converts HttpErrorResponse to StandardizedErrorResponse format
 *
 * This ensures consistent error handling across the application
 * and provides meaningful messages to users and developers
 */
function standardizeHttpError(error: HttpErrorResponse): StandardizedErrorResponse {
  // Try to extract message from error response body first
  let message = error.error?.message || error.error?.error || error.statusText || 'An error occurred';
  let errorCode = error.error?.error || error.error?.code || 'HTTP_ERROR';

  // Skip network errors (status 0)
  if (error.status === 0) {
    return {
      success: false,
      statusCode: 0,
      message: 'Network error - Unable to reach the server. Please check your connection.',
      error: 'NETWORK_ERROR',
      timestamp: new Date().toISOString(),
      path: error.url || 'unknown',
    };
  }

  // Provide specific messages based on HTTP status codes
  switch (error.status) {
    // ============================================
    // 401 Unauthorized - Authentication Errors
    // ============================================
    case 401:
      // Check if it's specifically a token expiration error
      if (
        error.error?.message?.toLowerCase().includes('expired') ||
        error.error?.error?.toLowerCase().includes('expired') ||
        errorCode.includes('EXPIRED')
      ) {
        message = 'Your authentication token has expired. Please log in again.';
        errorCode = 'TOKEN_EXPIRED_OR_INVALID';
      } else if (
        error.error?.message?.toLowerCase().includes('invalid') ||
        error.error?.error?.toLowerCase().includes('invalid')
      ) {
        message = 'Your authentication token is invalid. Please log in again.';
        errorCode = 'TOKEN_EXPIRED_OR_INVALID';
      } else {
        // Generic 401 - token might be expired or invalid
        message = message || 'Authentication failed. Your session may have expired. Please log in again.';
        errorCode = errorCode || 'TOKEN_EXPIRED_OR_INVALID';
      }
      break;

    // ============================================
    // 403 Forbidden - Permission Errors
    // ============================================
    case 403:
      message = 'You do not have permission to access this resource.';
      errorCode = 'INSUFFICIENT_PERMISSIONS';
      break;

    // ============================================
    // 404 Not Found - Resource Errors
    // ============================================
    case 404:
      message = message || 'The requested resource was not found.';
      errorCode = errorCode || 'NOT_FOUND';
      break;

    // ============================================
    // 400 Bad Request - Client Errors
    // ============================================
    case 400:
      message = message || 'Invalid request. Please check your input and try again.';
      errorCode = errorCode || 'BAD_REQUEST';
      break;

    // ============================================
    // 429 Too Many Requests - Rate Limiting
    // ============================================
    case 429:
      message = 'Too many requests. Please wait a moment before trying again.';
      errorCode = 'RATE_LIMITED';
      break;

    // ============================================
    // 500-599 Server Errors
    // ============================================
    case 500:
      message = 'Internal server error. Please try again later.';
      errorCode = 'INTERNAL_SERVER_ERROR';
      break;

    case 502:
      message = 'Bad Gateway. The server is temporarily unavailable. Please try again later.';
      errorCode = 'BAD_GATEWAY';
      break;

    case 503:
      message = 'Service Unavailable. The server is temporarily down. Please try again later.';
      errorCode = 'SERVICE_UNAVAILABLE';
      break;

    // ============================================
    // Other 5xx errors
    // ============================================
    default:
      if (error.status >= 500) {
        message = message || 'Server error. Please try again later.';
        errorCode = errorCode || 'SERVER_ERROR';
      }
  }

  // Build the standardized error response
  return {
    success: false,
    statusCode: error.status,
    message,
    error: errorCode,
    timestamp: new Date().toISOString(),
    path: error.url || 'unknown',
  };
}
